import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/ContentWrapper";
import { ReactComponent as AddIcon } from "../../assets/svg/add-icon.svg";
import { ReactComponent as ExpertIcon } from "../../assets/svg/expert-filter.svg";
import { ReactComponent as ProductIcon } from "../../assets/svg/product-filter.svg";
import { ReactComponent as BrandIcon } from "../../assets/svg/brand-filter.svg";
import { ReactComponent as NoVideoIcon } from "../../assets/svg/no-video.svg";
import { ReactComponent as StatusIcon } from "../../assets/svg/status.svg";
import { ReactComponent as OrderIcon } from "../../assets/svg/order-filter.svg";
import { ReactComponent as ContentTypeIcon } from "../../assets/svg/content-type-filter.svg";
import { ReactComponent as ChannelIcon } from "../../assets/svg/channel-filter.svg";
import { DropdownMenu } from "../../components/Dropdown/Dropdown";
import { MenuIcon } from "../../components/MenuOptions/MenuIcon";

import ProjectsSwitcher from "./components/ProjectsSwitcher";
import ProjectFiltersModal from "./components/ProjectFiltersModal";
import ProjectListItem from "./components/ProjectListItem";

import AddProjectModal from "./components/AddProjectModal";
import DownloadProjectCsv from "./components/DownloadProjectCsv";
import axiosInstant from "../../api";
import {
  getAllBrandProjects,
  getBrandProjects,
  getBrandOrders,
  getBrandOrdersByBrand,
  getContentTypes,
  getContentChannels,
} from "../../api/endpoints";
import { DotLottleLoader, LoadMoreBtn } from "../../components/Loader";
import {
  sortBrandsByRmn,
  BRAND_PROJECT_STATUSES,
  getAssociationIds,
} from "./helper";
import { BrandProject } from "./components/constants";
import { isDeletedProjectRow } from "./components/projectSoftDelete";
import { ProjectLegend } from "./components/projectLegend";
import { useUser } from "../../hooks/useUser";
import ExpertSelectionRecommendationDialog from "./expertSelection/ExpertSelectionRecommendationDialog";
import { ListPageHeader } from "../../components/ListPageHeader/ListPageHeader";
import SortControls from "../../components/SortControls/SortControls";
import { useCommonParams } from "../../hooks/useCommonParams";
import { usePagination } from "../../hooks/usePagination";
import { sortData } from "../../utils/sortUtils";
import { isAM, isBrand, isRMN } from "../../utils";
import { PageDivider } from "../../components/Loader";
import { useWorkspaceBrandId } from "../../hooks/useWorkspaceBrandId";

const PAGE_SIZE = 20;

const VideoProduction = () => {
  const { user, role } = useUser();
  // Non-null only when internal staff have opened a client workspace.
  const workspaceBrandId = useWorkspaceBrandId();
  const scopedListBrandId = workspaceBrandId || user?.brand?.[0]?.id;
  const timeoutId = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    tab,
    setTab,
    sortType,
    resolvedSortKey,
    setSortDirection,
    hasAnyFilter,
    clearAllFilters,
    clearFilter,
    setFilter,
    getFilter,
  } = useCommonParams({ sortKeyMap: { dateCreated: "created_at" } });

  const selectedExpert = useMemo(
    () => getFilter("expertId", ""),
    [searchParams],
  );
  const selectedProduct = useMemo(
    () => getFilter("productId", ""),
    [searchParams],
  );
  const selectedBrand = useMemo(
    () => getFilter("brandId") || undefined,
    [searchParams],
  );
  const selectedStatus = useMemo(() => {
    const status = getFilter("status", "");
    if (status && status !== "active" && status !== "complete") {
      return status;
    }
    return "";
  }, [searchParams]);

  const estTimeSetFilter = useMemo(() => getFilter("est_time_set", ""), [searchParams]);
  const filmWithinFilter = useMemo(() => getFilter("film_within", ""), [searchParams]);
  const exactStatusFilter = useMemo(() => getFilter("exactStatus", ""), [searchParams]);

  const selectedOrder = useMemo(() => getFilter("orderId", ""), [searchParams]);
  const selectedContentType = useMemo(
    () => getFilter("contentTypeId", ""),
    [searchParams],
  );
  const selectedChannel = useMemo(
    () => getFilter("channelId", ""),
    [searchParams],
  );

  const setSelectedExpert = (value: string) => setFilter("expertId", value);
  const setSelectedProduct = (value: string) => setFilter("productId", value);
  const setSelectedBrand = (value: string | undefined) =>
    setFilter("brandId", value);
  const setSelectedOrder = (value: string) => setFilter("orderId", value);
  const setSelectedContentType = (value: string) =>
    setFilter("contentTypeId", value);
  const setSelectedChannel = (value: string) => setFilter("channelId", value);
  const setSelectedStatus = (value: string) => {
    if (value && value !== "active" && value !== "complete") {
      setFilter("status", value);
    } else {
      clearFilter("status");
    }
  };
  // Admin / AM admin only: soft-deleted projects (GET ...?deleted=1). Anyone
  // else with ?tab=deleted in the URL gets the normal list (the API ignores
  // the param for them too).
  const canViewDeleted = isAM(role);
  const isDeletedView = canViewDeleted && tab === "deleted";

  const isActive = useMemo(() => {
    if (tab === "complete") return false;
    if (tab === "active") return true;

    const status = getFilter("status");
    if (status) {
      if (status === "complete") {
        return false;
      }
      if (status === "active" || status === "ACTIVE") {
        return true;
      }
      const completeStatuses = [BRAND_PROJECT_STATUSES.FINAL_DELIVERED];
      if (completeStatuses.includes(status as any)) {
        return false;
      }
      return true;
    }

    return true;
  }, [tab, getFilter]);

  const setIsActive = (active: boolean) => {
    // The Deleted view's status filter offers every status; Cancelled has no
    // place in the Active / Final Delivered lists, so drop it on the way out.
    const leavingDeletedWithCancelled =
      isDeletedView && selectedStatus === BRAND_PROJECT_STATUSES.CANCELLED;
    setTab(active ? "active" : "complete");
    // An ?edit= picked up in the Deleted view names a deleted project; drop
    // it so the edit modal doesn't open over the live list.
    if (isDeletedView) clearFilter("edit");

    const currentStatus = getFilter("status");
    if (selectedStatus) {
      const completeStatuses = [BRAND_PROJECT_STATUSES.FINAL_DELIVERED];
      const isStatusComplete = completeStatuses.includes(selectedStatus as any);

      if (
        (active && isStatusComplete) ||
        (!active && !isStatusComplete) ||
        leavingDeletedWithCancelled
      ) {
        clearFilter("status");
      }
    } else if (
      (active && currentStatus === "active") ||
      (!active && currentStatus === "complete")
    ) {
      clearFilter("status");
    }
  };

  const showDeletedView = () => {
    setTab("deleted");
    clearFilter("edit");
  };

  const [showCsvDialog, setShowCsvDialog] = useState(false);
  const [products, setProducts] = useState([]);
  const [experts, setExperts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [orderOptions, setOrderOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [contentTypeOptions, setContentTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [channelOptions, setChannelOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const filterKeys = [
    "expertId",
    "productId",
    "brandId",
    "status",
    "orderId",
    "contentTypeId",
    "channelId",
  ] as const;

  const activeFiltersCount = useMemo(() => {
    return filterKeys.filter((key) => {
      if (key === "brandId") return !!selectedBrand;
      if (key === "status") return !!selectedStatus;
      return !!getFilter(key);
    }).length;
  }, [searchParams]);

  const isInternalUser =
    user?.usertype === "admin" ||
    user?.usertype === "pro team admin" ||
    user?.usertype === "account manager admin" ||
    user?.usertype === "sales" ||
    user?.usertype === "post production admin";

  const buildQueryString = useCallback((pageNum: number) => {
    const params = new URLSearchParams();
    params.append("limit", PAGE_SIZE.toString());
    if (pageNum > 1) {
      params.append("page", pageNum.toString());
    }

    params.append("search", searchText);
    if (selectedExpert) params.append("expertId", selectedExpert);
    if (selectedProduct) params.append("productId", selectedProduct);
    if (selectedBrand) params.append("brandId", selectedBrand);
    if (selectedOrder) params.append("orderId", selectedOrder);
    if (selectedContentType)
      params.append("contentTypeId", selectedContentType);
    if (selectedChannel) params.append("channelId", selectedChannel);
    if (selectedStatus) {
      params.append("status", selectedStatus);
    } else if (!isDeletedView && isActive !== null) {
      // The Deleted view spans every status unless one is picked.
      params.append("status", isActive ? "active" : "complete");
    }
    if (estTimeSetFilter) params.append("est_time_set", estTimeSetFilter);
    if (filmWithinFilter) params.append("film_within", filmWithinFilter);
    if (exactStatusFilter) params.append("exactStatus", exactStatusFilter);
    if (isDeletedView) params.append("deleted", "1");

    return params.toString();
  }, [searchText, selectedExpert, selectedProduct, selectedBrand, selectedOrder, selectedContentType, selectedChannel, selectedStatus, isActive, isDeletedView, estTimeSetFilter, filmWithinFilter, exactStatusFilter]);

  const fetchFn = useCallback(async (pageNum: number) => {
    try {
      const queryString = buildQueryString(pageNum);
      let response;

      const projectsBrandId = workspaceBrandId || user?.brand?.[0]?.id;
      if (isDeletedView) {
        // Deleted rows come from the admin list (GET /brand-projects?deleted=1);
        // inside a client workspace, scope it to that brand.
        const params = new URLSearchParams(queryString);
        if (projectsBrandId) params.set("brandId", String(projectsBrandId));
        response = await axiosInstant.get(
          `${getAllBrandProjects()}?${params.toString()}`,
        );
      } else if (projectsBrandId) {
        response = await axiosInstant.get(
          `${getBrandProjects(projectsBrandId)}?${queryString}`,
        );
      } else {
        response = await axiosInstant.get(
          `${getAllBrandProjects()}?${queryString}`,
        );
      }

      const rows = sortBrandsByRmn(response.data.rows || []);
      return { rows, count: response.data.count || 0, error: null };
    } catch (e) {
      return { rows: [] as BrandProject[], count: 0, error: (e as Error).message };
    }
  }, [buildQueryString, user, isDeletedView, workspaceBrandId]);

  const {
    items: projects,
    totalCount: count,
    loading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    error,
    initLoad,
    loadMore,
    loadPrevious,
    silentRefresh,
    updateItem,
    topSentinelRef,
    editingItem: editingProject,
    closeEdit: closeEditProject,
  } = usePagination<BrandProject>({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["sort", "view", "page", "create", "expertSelection", "edit"],
    },
    editDeepLink: { getId: (p) => p.id },
  });

  const showAddProjectModal = () => {
    setFilter("create", "new");
  };

  const hideProjectModal = () => {
    clearFilter("create");
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let endpoint = "/products?projects=true&all=true";
        // Internal usertypes are excluded here by design, EXCEPT inside a
        // workspace, where seeing the client's own list is the point.
        if (
          scopedListBrandId &&
          (workspaceBrandId ||
            (user?.usertype !== "admin" &&
              user?.usertype !== "pro team admin" &&
              user?.usertype !== "account manager admin" &&
              user?.usertype !== "sales" &&
              user?.usertype !== "post production admin"))
        ) {
          endpoint = `/brands/${scopedListBrandId}/products?projects=true&all=true`;
        }

        const response = await axiosInstant.get(endpoint);
        setProducts(response.data?.rows || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, [user]);

  useEffect(() => {
    const fetchExperts = async () => {
      try {
        let endpoint = "/users/pros?projects=true";
        // Internal usertypes are excluded here by design, EXCEPT inside a
        // workspace, where seeing the client's own list is the point.
        if (
          scopedListBrandId &&
          (workspaceBrandId ||
            (user?.usertype !== "admin" &&
              user?.usertype !== "pro team admin" &&
              user?.usertype !== "account manager admin" &&
              user?.usertype !== "sales" &&
              user?.usertype !== "post production admin"))
        ) {
          endpoint = `/brands/${scopedListBrandId}/pros?projects=true`;
        }

        const response = await axiosInstant.get(endpoint);
        setExperts(response.data?.rows || []);
      } catch (error) {
        console.error("Error fetching experts:", error);
      }
    };
    fetchExperts();
  }, [user]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        if (
          user?.usertype === "admin" ||
          user?.usertype === "pro team admin" ||
          user?.usertype === "account manager admin" ||
          user?.usertype === "sales" ||
          user?.usertype === "post production admin"
        ) {
          const response = await axiosInstant.get(
            "/brands?projects=true&all=true",
          );
          setBrands(response.data?.rows || []);
        } else {
          setBrands([]);
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    };
    fetchBrands();
  }, [user]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [ordersRes, contentTypesRes, channelsRes] =
          await Promise.all([
            workspaceBrandId || user?.brand?.[0]?.id
              ? axiosInstant.get(
                  getBrandOrdersByBrand(
                    workspaceBrandId || user!.brand[0].id,
                  ),
                )
              : axiosInstant.get(getBrandOrders()),
            axiosInstant.get(getContentTypes()),
            axiosInstant.get(getContentChannels()),
          ]);

        setOrderOptions(
          (ordersRes.data?.rows || ordersRes.data || []).map((o: any) => ({
            label: o.name,
            value: String(o.id),
            badge: getAssociationIds({
              id: o.id,
              product: { title: o.brand?.name || "" },
              order: o.sow ? { sow: o.sow } : undefined,
            }),
          })),
        );
        setContentTypeOptions(
          (contentTypesRes.data || []).map((ct: any) => ({
            label: ct.name,
            value: String(ct.id),
          })),
        );
        setChannelOptions(
          (channelsRes.data || []).map((ch: any) => ({
            label: ch.name,
            value: String(ch.id),
          })),
        );
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };
    fetchFilterOptions();
  }, [user]);

  const expertOptions = experts.map((expert: any) => ({
    label: expert.displayName,
    value: expert.userid,
  }));

  const productOptions = products.map((product: any) => ({
    label: product.title,
    value: product.desireid,
  }));

  const brandOptions = brands.map((brand: any) => ({
    label: brand.name,
    value: String(brand.id),
  }));

  const statusEntries = () => {
    const all = Object.entries(BRAND_PROJECT_STATUSES);
    // A project can be deleted at any status, Cancelled included.
    if (isDeletedView) return all;
    return isActive
      ? all.filter(([key]) => key !== "CANCELLED" && key !== "FINAL_DELIVERED")
      : all.filter(([key]) => key === "FINAL_DELIVERED");
  };

  const statusOptions = statusEntries().map(([key, value]) => ({
    label: value as string,
    value: value as string,
  }));

  const filterConfigs = useMemo(
    () => [
      {
        labelId: "expert",
        name: "expert",
        value: selectedExpert,
        onChange: setSelectedExpert,
        options: expertOptions,
        placeholder: "Expert",
        placeholderIcon: <ExpertIcon />,
      },
      {
        labelId: "product",
        name: "product",
        value: selectedProduct,
        onChange: setSelectedProduct,
        options: productOptions,
        placeholder: "Product",
        placeholderIcon: <ProductIcon />,
      },
      {
        labelId: "status",
        name: "status",
        value: selectedStatus,
        onChange: setSelectedStatus,
        options: statusOptions,
        placeholder: "Status",
        placeholderIcon: <StatusIcon />,
      },
      {
        labelId: "brand",
        name: "brand",
        value: selectedBrand || "",
        onChange: setSelectedBrand,
        options: brandOptions,
        placeholder: "Brand",
        placeholderIcon: <BrandIcon />,
        visible: isInternalUser,
      },
      {
        labelId: "order",
        name: "order",
        value: selectedOrder,
        onChange: setSelectedOrder,
        options: orderOptions,
        placeholder: "Order",
        placeholderIcon: <OrderIcon />,
      },
      {
        labelId: "contentType",
        name: "contentType",
        value: selectedContentType,
        onChange: setSelectedContentType,
        options: contentTypeOptions,
        placeholder: "Content Type",
        placeholderIcon: <ContentTypeIcon />,
      },
      {
        labelId: "channel",
        name: "channel",
        value: selectedChannel,
        onChange: setSelectedChannel,
        options: channelOptions,
        placeholder: "Channel",
        placeholderIcon: <ChannelIcon />,
      },
    ],
    [
      selectedExpert,
      selectedProduct,
      selectedStatus,
      selectedBrand,
      selectedOrder,
      selectedContentType,
      selectedChannel,
      expertOptions,
      productOptions,
      statusOptions,
      brandOptions,
      orderOptions,
      contentTypeOptions,
      channelOptions,
      isInternalUser,
    ],
  );

  const statusChangeRef = useRef(false);

  useEffect(() => {
    // The Deleted view is its own tab: picking a status there must not flip
    // it to Active / Final Delivered.
    if (isDeletedView) return;
    if (selectedStatus) {
      const completeStatuses = [BRAND_PROJECT_STATUSES.FINAL_DELIVERED];
      const isStatusComplete = completeStatuses.includes(
        selectedStatus as any,
      );
      statusChangeRef.current = true;
      if (isStatusComplete && isActive) {
        setIsActive(false);
      } else if (!isStatusComplete && !isActive) {
        setIsActive(true);
      }
    }
  }, [selectedStatus, isActive, isDeletedView]);

  const handleClearFilters = () => {
    clearAllFilters([
      "expertId",
      "productId",
      "brandId",
      "status",
      "orderId",
      "contentTypeId",
      "channelId",
    ]);
    setSearchText("");
  };

  const sortedProjects = useMemo(() => {
    return sortData(projects, resolvedSortKey, sortType.isAscending);
  }, [projects, resolvedSortKey, sortType.isAscending]);

  let emptyMessage = "No completed projects yet. Once you finish a project, it will appear here.";
  if (isDeletedView) {
    emptyMessage = "No deleted projects. A deleted project shows up here until an admin restores it.";
  } else if (isActive) {
    emptyMessage = "No active projects found. Start a new order to get things rolling!";
  }

  return (
    <>
      <MainContainer>
        <ListPageHeader
          title="Projects"
          titleExtra={<ProjectLegend />}
          searchValue={searchText}
          onSearchChange={setSearchText}
          searchInputId="search-projects"
          searchRightContent={
            <>
              <SortControls
                isAscending={sortType.isAscending}
                onDirectionChange={setSortDirection}
              />
              <ProjectFiltersModal
                filters={filterConfigs}
                activeFiltersCount={activeFiltersCount}
                onClearFilters={handleClearFilters}
              />
              {/* The CSV export covers live projects only. */}
              {isInternalUser && !isDeletedView && (
                <DropdownMenu
                  trigger={<MenuIcon id="export-menu" isDetailedPage />}
                  options={[
                    {
                      label: 'Export CSV',
                      onClick: () => setShowCsvDialog(true),
                    },
                  ]}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
              )}
            </>
          }
        />

        <div className="w-full flex items-start gap-4">
          <div className="flex-shrink-0">
            <ProjectsSwitcher
              isActive={isActive}
              onActiveClick={() => setIsActive(true)}
              onCompleteClick={() => setIsActive(false)}
              isDeleted={isDeletedView}
              onDeletedClick={canViewDeleted ? showDeletedView : undefined}
            />
          </div>
        </div>

        <PageDivider
          count={count}
          label={isDeletedView ? "Deleted Projects" : "Projects"}
          className="mt-[24px] mb-[24px]"
          canLoadPrevious={canLoadPrevious}
          hasMore={hasMore}
          onLoadPrevious={loadPrevious}
        />

        {/* eslint-disable-next-line no-nested-ternary */}
        {loading && projects.length === 0 ? (
          <div className="flex justify-center items-center mt-20">
            <DotLottleLoader fill={false} />
          </div>
        ) : sortedProjects.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center gap-6 mt-40">
            <NoVideoIcon />
            <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic">
              {emptyMessage}
            </div>
            {!isDeletedView && (
              <button
                type="button"
                onClick={() => navigate("/video-production/orders?create=new")}
                className="flex items-center gap-1 bg-accent text-base text-white py-3 px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline"
              >
                <AddIcon className="mr-1" />
                {isBrand(role) || isRMN(user) ? 'Request A Quote' : 'New Order'}
              </button>
            )}
          </div>
        ) : (
          <>
            {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
            {loadingPrevious && (
              <div className="flex justify-center py-2">
                <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
              </div>
            )}
            <div className="flex flex-col gap-4">
              {sortedProjects.map((project, index) => (
                <ProjectListItem
                  key={index}
                  project={project}
                  onUpdate={() => initLoad(1)}
                  deleted={isDeletedProjectRow(project, isDeletedView)}
                />
              ))}
            </div>

            {hasMore && (
              <LoadMoreBtn onClick={loadMore} loading={loading} />
            )}
          </>
        )}
      </MainContainer>

      {getFilter("create") === "new" && (
        <AddProjectModal
          setOpen={hideProjectModal}
          fetchProjects={() => initLoad(1)}
        />
      )}

      {/* No editing a deleted project, even from a stale ?edit= link. */}
      {editingProject && !isDeletedView && (
        <AddProjectModal
          setOpen={() => { closeEditProject(); }}
          fetchProjects={(updated?: BrandProject) => {
            const id = editingProject?.id;
            closeEditProject();
            if (id && updated && String(updated.id) === String(id)) {
              updateItem(id, (existing) => sortBrandsByRmn([{ ...existing, ...updated }])[0]);
            } else {
              silentRefresh();
            }
          }}
          project={editingProject}
          isEdit
        />
      )}

      {getFilter("expertSelection") && (
        <ExpertSelectionRecommendationDialog
          onConfirm={() => initLoad(1)}
        />
      )}

      {showCsvDialog && (
        <DownloadProjectCsv
          showDialog={showCsvDialog}
          setShowDialog={setShowCsvDialog}
          filters={{
            search: searchText || undefined,
            status: isActive ? "active" : "complete",
            expertId: selectedExpert || undefined,
            productId: selectedProduct || undefined,
            brandId: selectedBrand || undefined,
            orderId: selectedOrder || undefined,
            contentTypeId: selectedContentType || undefined,
            channelId: selectedChannel || undefined,
          }}
        />
      )}
    </>
  );
};

export default VideoProduction;
